/**
 * IXVerify v2 — the dispense wizard (ixv2 WP-3.2; plan §3.2/§4).
 *
 * Full-screen (the one allowed full-screen surface, R7) with the slim wizard
 * header. One continuous engine for both entry modes:
 *   - LIVE CAPTURE: "New Dispense" pre-creates the session and opens the
 *     viewfinder directly (1 tap). Capture is order-free; every asset uploads
 *     and processes while the CRC keeps shooting (R11 — extraction fires the
 *     moment a page's rendition lands, never after label scanning).
 *   - STAGED: opened from the Ready-to-dispense queue with extraction already
 *     green — straight to kit scanning → one attestation tap → published.
 *
 * The server owns the state machine (WP-3.1); this page reflects it and
 * drives the R12 loops (retake / add-page, originals-block-attestation) and
 * the R5 gates (amber fields need an explicit human tap; mismatches never
 * auto-resolve).
 *
 * CROSS-DEVICE, step A (2026-09-18): one server session, two screens. The
 * same user signed in on a desktop and a phone works ONE session — drop the
 * IXRS PDF here on the desktop, photograph the kits on the phone, attest
 * from either. Nothing pairs the devices: the wizard listens to the
 * `ixv_session_update` tick the server already publishes on the creator's
 * channel and refetches its own session (debounced, sequenced, never moving
 * this device off its current surface); the server settles page indexes so
 * two devices cannot collide; and the whole wizard is a drop target while
 * capturing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Plus, UploadCloud, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../ui';
import { subscribeIxvSessionUpdates } from '../../lib/realtimeTransport';
import * as ixv from '../lib/api';
import {
  attestPlan,
  capturePrompt,
  confirmPayloadFromExtraction,
  derivePages,
  kitProgress,
  REVIEW_FIELDS,
  stepForSession,
  type CapturePage,
  type ReviewFieldName,
  type WizardStep,
} from '../lib/captureMachine';
import { CaptureUploader, type UploadTask } from '../lib/uploadQueue';
import { makeRendition, type CapturedFrame } from '../lib/image';
import WizardHeader from '../components/wizard/WizardHeader';
import CaptureSurface, { type CaptureMode } from '../components/wizard/CaptureSurface';
import ReviewScreen, { FIELD_LABEL } from '../components/wizard/ReviewScreen';
import AttestScreen from '../components/wizard/AttestScreen';
import { MismatchRecordedScreen, SuccessScreen } from '../components/wizard/ResultScreen';
import SubjectSheet from '../components/wizard/SubjectSheet';
import SecondCheckSheet from '../components/wizard/SecondCheckSheet';
import ReportPreview from '../components/shared/ReportPreview';

const fieldInputCls =
  'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 motion-reduce:transition-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';

/** Remote ticks land back-to-back (ingest, then extraction) — coalesce them. */
export const REMOTE_TICK_DEBOUNCE_MS = 250;

/** Files the wizard accepts from a drop — the same list as the native inputs. */
const DROP_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

/** True when a drag carries files (as opposed to text/links from the page). */
function dragHasFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types || []).includes('Files');
}

/** A tick whose `updated_at` is not newer than what we hold is our own action
 *  echoing back (every mutation bumps the row's updated_at). Unparsable on
 *  either side ⇒ refetch — never skip on a guess. */
export function isNewerThanHeld(tick: string | null | undefined, held: string | null | undefined): boolean {
  const t = tick ? Date.parse(tick) : NaN;
  const h = held ? Date.parse(held) : NaN;
  if (Number.isNaN(t) || Number.isNaN(h)) return true;
  return t > h;
}

export default function Dispense() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentSite } = useAuth();
  const { toast } = useToast();

  const [sessionId, setSessionId] = useState<string | null>(routeId || null);
  const [detail, setDetail] = useState<ixv.SessionDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<'capture' | 'review' | 'attest' | null>(null);
  const [mode, setMode] = useState<CaptureMode>('document');

  // Capture bookkeeping
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const uploaderRef = useRef<CaptureUploader | null>(null);
  const pageIndexRef = useRef(0);
  /** Sequence number of the latest detail fetch issued (see refreshDetail). */
  const fetchSeqRef = useRef(0);
  const [extracting, setExtracting] = useState(false);
  const extractQueuedRef = useRef(false);
  const [readingLabel, setReadingLabel] = useState(false);
  const [lastKitTick, setLastKitTick] = useState<string | null>(null);
  const [promptDismissedAt, setPromptDismissedAt] = useState<string | null>(null);
  const [replaceKitId, setReplaceKitId] = useState<string | null>(null);
  const kitQueueRef = useRef<Promise<void>>(Promise.resolve());
  // Pilot r1: local page previews (object URLs, fresh captures only) + the
  // per-page soft-discard / capture-override in-flight flags.
  const [pagePreviews, setPagePreviews] = useState<Record<number, string>>({});
  const pagePreviewsRef = useRef<Record<number, string>>({});
  pagePreviewsRef.current = pagePreviews;
  const [discardBusyKey, setDiscardBusyKey] = useState<string | null>(null);
  const [overridingCapture, setOverridingCapture] = useState(false);

  // Review bookkeeping (R5)
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Partial<Record<ReviewFieldName, string | null>>>({});
  // Pilot r4: pre-confirm edits to the PROPOSED assigned-kit list (null =
  // the extraction proposal untouched). Rides the confirm call — the earliest
  // point a misread kit number can be fixed, before anything is recorded.
  const [kitDraft, setKitDraft] = useState<string[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [subjectMatch, setSubjectMatch] = useState<'matched' | 'unknown' | 'no_study' | null>(null);
  const [teaching, setTeaching] = useState(false);

  // Attest / publish
  const [publishing, setPublishing] = useState(false);
  const [attestError, setAttestError] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<ixv.PublishResult | null>(null);

  // Sheets
  const [subjectSheetOpen, setSubjectSheetOpen] = useState(false);
  /** 'add' = unknown subject (attach + create); 'dob' = known roster row with
   *  no DOB (2026-09-04 — CRIO had none on file) → PATCH the roster row. */
  const [subjectSheetMode, setSubjectSheetMode] = useState<'add' | 'dob'>('add');
  const [subjectSaving, setSubjectSaving] = useState(false);
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [manualKitOpen, setManualKitOpen] = useState(false);
  const [manualKitNumber, setManualKitNumber] = useState('');
  const [manualKitReason, setManualKitReason] = useState('');
  // Pilot r3: mismatch reopen + assigned-kit correction (the misread case).
  const [reopening, setReopening] = useState(false);
  const [assignedEditOpen, setAssignedEditOpen] = useState(false);
  const [assignedKitEdits, setAssignedKitEdits] = useState<string[]>([]);
  // Pilot r5: the same sheet corrects the four assignment FIELDS (protocol,
  // subject, visit, date) — previously a confirmed field was uneditable.
  const [assignedFieldEdits, setAssignedFieldEdits] = useState<Record<ReviewFieldName, string>>({
    protocol_id: '',
    subject_number: '',
    visit_name: '',
    dispense_date: '',
  });
  const [savingAssignedKits, setSavingAssignedKits] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [secondCheckOpen, setSecondCheckOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const detailRef = useRef<ixv.SessionDetail | null>(null);
  detailRef.current = detail;
  const editsRef = useRef(edits);
  editsRef.current = edits;

  // Every detail fetch is sequenced: an out-of-order response (a tick-driven
  // refetch resolving after a later own-action refresh) is dropped instead of
  // regressing the view. The fetched detail is always returned for callers
  // that act on it directly.
  const refreshDetail = useCallback(async (id: string) => {
    const seq = ++fetchSeqRef.current;
    const fresh = await ixv.getSession(id);
    if (seq === fetchSeqRef.current) {
      setDetail(fresh);
      // Never number a new page below what the server already holds — the
      // other device may have captured pages since this one booted.
      const maxPage = Math.max(-1, ...fresh.assets.filter((a) => a.page_index != null).map((a) => a.page_index as number));
      pageIndexRef.current = Math.max(pageIndexRef.current, maxPage + 1);
    }
    return fresh;
  }, []);

  // -- session creation / load ---------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        if (routeId) {
          setSessionId(routeId);
          // The boot fetch joins the same sequence as every refresh: a remote
          // tick that refetched while this was in flight must not be undone
          // by an older response landing later.
          const seq = ++fetchSeqRef.current;
          const d = await ixv.getSession(routeId);
          if (cancelled || seq !== fetchSeqRef.current) return;
          setDetail(d);
          // WP-5.1: a resumed session restores its teaching-banner state from
          // the persisted extraction payload.
          if (typeof d.session.extraction?.teaching_mode === 'boolean') {
            setTeaching(d.session.extraction.teaching_mode);
          }
          // Resume paging after the highest stored page (multi-page add-page).
          const maxPage = Math.max(-1, ...d.assets.filter((a) => a.page_index != null).map((a) => a.page_index as number));
          pageIndexRef.current = Math.max(pageIndexRef.current, maxPage + 1);
        } else {
          // §3.2 step 1: session pre-created the moment the wizard opens.
          const session = await ixv.createSession();
          if (cancelled) return;
          setSessionId(session.id);
          navigate(`/ixverify/dispense/${session.id}`, { replace: true });
          setDetail({ session, kits: [], kit_state: { assigned_count: 0, counts: {} as any, all_resolved: false, pass: false }, assets: [], subject: null });
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            ixv.isKillSwitched(err) ? 'IXVerify is currently disabled.' : ixv.errorMessage(err, 'Could not open the dispense session')
          );
        }
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // -- cross-device live sync (step A, 2026-09-18) -------------------------
  // Every session mutation publishes `ixv_session_update` on the creator's
  // `ixverify:{userId}` channel (SSE in prod; a no-op adapter in local
  // Supabase mode). The same user signed in on a second device receives it
  // too, so a PDF dropped on the desktop or a kit scanned on the phone shows
  // up here without a tap. Rules:
  //   - only THIS session's ticks count, and one whose `updated_at` is not
  //     newer than what we hold is skipped (our own action already refreshed);
  //   - refetches are debounced + coalesced (ingest and extraction ticks land
  //     back-to-back) and sequenced through refreshDetail;
  //   - a remote update never moves this device off its current surface: the
  //     local `view` is pinned before applying, so a remote confirm does not
  //     yank a phone out of Kits mode mid-scan. Terminal states (published /
  //     mismatch / cancelled) still take over through stepForSession;
  //   - CATCH-UP ON WAKE (landing review, 2026-09-18): a tick only reaches an
  //     OPEN stream, the server replays nothing a closed stream missed (no
  //     event ids), and the multiplexer reconnects silently — so a phone that
  //     locked its screen, went to the background, rode the stream's max-age
  //     rollover or a network blip has lost every change the other device made
  //     meanwhile. Coming back into view or focus therefore refetches
  //     UNCONDITIONALLY (the echo-skip applies to ticks only); the debounce and
  //     the sequencing make a redundant fetch harmless.
  useEffect(() => {
    if (!user?.id || !sessionId) return undefined;
    const id = sessionId;
    const state = { timer: null as ReturnType<typeof setTimeout> | null, inflight: false, dirty: false, cancelled: false };
    function schedule() {
      if (state.timer) return;
      state.timer = setTimeout(() => {
        state.timer = null;
        void run();
      }, REMOTE_TICK_DEBOUNCE_MS);
    }
    async function run() {
      if (state.cancelled) return;
      if (state.inflight) {
        state.dirty = true;
        return;
      }
      state.inflight = true;
      try {
        if (detailRef.current) {
          const current = stepForSession(detailRef.current) === 'review' ? 'review' : 'capture';
          setView((v) => v ?? current);
        }
        const fresh = await refreshDetail(id);
        if (typeof fresh.session.extraction?.teaching_mode === 'boolean') setTeaching(fresh.session.extraction.teaching_mode);
      } catch {
        /* keep the current view — the next tick retries */
      } finally {
        state.inflight = false;
        if (state.dirty && !state.cancelled) {
          state.dirty = false;
          schedule();
        }
      }
    }
    const unsubscribe = subscribeIxvSessionUpdates<ixv.IxvSessionSummary>(user.id, (summary) => {
      if (!summary || summary.id !== id) return;
      if (!isNewerThanHeld(summary.updated_at, detailRef.current?.session.updated_at)) return;
      schedule();
    });
    // Wake = this page is (back) in view or focused. Going hidden is not a wake.
    const wake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      schedule();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    window.addEventListener('pageshow', wake);
    return () => {
      state.cancelled = true;
      if (state.timer) clearTimeout(state.timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      window.removeEventListener('pageshow', wake);
      unsubscribe();
    };
  }, [user?.id, sessionId, refreshDetail]);

  // -- extraction (fires while capture continues — R11) ---------------------
  const runExtraction = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    if (extracting) {
      extractQueuedRef.current = true;
      return;
    }
    setExtracting(true);
    try {
      const result = await ixv.extractSession(id);
      setSubjectMatch(result.subject_match);
      // WP-5.1: the server derives the §5.4 teaching state from the study's
      // learning profile — the banner shows from the first extraction on.
      if (typeof result.teaching_mode === 'boolean') setTeaching(result.teaching_mode);
      setPromptDismissedAt(null); // fresh verdict → fresh prompt
      await refreshDetail(id);
    } catch (err) {
      if (ixv.isKillSwitched(err)) {
        toast('IXVerify AI is currently disabled — extraction unavailable', { type: 'error' });
      } else if (ixv.errorCode(err) === 'IXV_NO_DOCUMENT') {
        // Nothing to extract yet — quiet.
      } else {
        toast(ixv.errorMessage(err, 'Could not read the assignment'), { type: 'error' });
      }
    } finally {
      setExtracting(false);
      if (extractQueuedRef.current) {
        extractQueuedRef.current = false;
        // A later page landed mid-run — run again so the verdict covers it.
        setTimeout(() => void runExtraction(), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, extracting, refreshDetail, toast]);

  const runExtractionRef = useRef(runExtraction);
  runExtractionRef.current = runExtraction;

  // -- the two-tier uploader -------------------------------------------------
  const uploader = useMemo(() => {
    if (!sessionId) return null;
    const up = new CaptureUploader({
      upload: async (blob, opts) => {
        const res = await ixv.uploadSessionAsset(sessionId, blob, {
          tier: opts.tier,
          kind: opts.kind,
          page_index: opts.page_index,
          link_asset_id: opts.link_asset_id,
          filename: opts.filename,
          source: opts.source,
        });
        return { asset: { id: res.asset.id, page_index: res.asset.page_index } };
      },
      onChange: (tasks) => setUploadTasks(tasks),
      // The server settled the page on another index (it collided with a page
      // the other device captured): move the local preview along and never
      // number a new page below the settled one.
      onPageIndexReassigned: (from, to) => {
        setPagePreviews((prev) => {
          if (!(from in prev)) return prev;
          const next = { ...prev, [to]: prev[from] };
          delete next[from];
          return next;
        });
        pageIndexRef.current = Math.max(pageIndexRef.current, to + 1);
      },
      // The extraction trigger: the RENDITION landing, never the original (R11).
      onRenditionUploaded: () => void runExtractionRef.current(),
      onOriginalUploaded: () => {
        const id = sessionId;
        if (id) void refreshDetail(id).catch(() => {});
      },
    });
    uploaderRef.current = up;
    return up;
  }, [sessionId, refreshDetail]);

  // -- capture handlers ------------------------------------------------------

  /** Keep a local preview for the thumbnail strip (jsdom-safe; fresh captures only). */
  const rememberPreview = useCallback((pageIndex: number, blob: Blob) => {
    try {
      if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
      const url = URL.createObjectURL(blob);
      setPagePreviews((prev) => ({ ...prev, [pageIndex]: url }));
    } catch {
      /* previews are a nicety — capture never depends on them */
    }
  }, []);

  const releasePreview = useCallback((pageIndex: number) => {
    setPagePreviews((prev) => {
      if (!(pageIndex in prev)) return prev;
      try {
        URL.revokeObjectURL(prev[pageIndex]);
      } catch { /* noop */ }
      const next = { ...prev };
      delete next[pageIndex];
      return next;
    });
  }, []);

  // Revoke every outstanding preview URL on unmount.
  useEffect(() => () => {
    for (const url of Object.values(pagePreviewsRef.current)) {
      try {
        URL.revokeObjectURL(url);
      } catch { /* noop */ }
    }
  }, []);

  const handleDocumentFrame = useCallback(
    async (frame: CapturedFrame) => {
      if (!uploader) return;
      const pageIndex = pageIndexRef.current++;
      let rendition: Blob | null = null;
      try {
        rendition = (await makeRendition(frame.blob)).blob;
      } catch {
        rendition = null; // original-only upload still works (server rasterizes)
      }
      rememberPreview(pageIndex, rendition || frame.blob);
      uploader.captureDocumentPage({ original: frame.blob, rendition, pageIndex, source: 'camera' });
    },
    [uploader, rememberPreview]
  );

  const handleDocumentFile = useCallback(
    async (file: File) => {
      const id = sessionId;
      if (!id) return;
      if (file.type === 'application/pdf') {
        try {
          await ixv.uploadSessionAsset(id, file, { kind: 'ixrs_document', tier: 'original', source: 'upload', filename: file.name });
          await refreshDetail(id);
          void runExtractionRef.current();
        } catch (err) {
          toast(ixv.errorMessage(err, 'Upload failed'), { type: 'error' });
        }
        return;
      }
      // Native-camera / library photo: original uploads UNTOUCHED (R12); the
      // rendition (canvas pass = EXIF normalized) drives extraction.
      if (!uploader) return;
      const pageIndex = pageIndexRef.current++;
      let rendition: Blob | null = null;
      try {
        rendition = (await makeRendition(file)).blob;
      } catch {
        rendition = null;
      }
      rememberPreview(pageIndex, rendition || file);
      uploader.captureDocumentPage({ original: file, rendition, pageIndex, source: 'upload' });
    },
    [sessionId, uploader, refreshDetail, toast, rememberPreview]
  );

  // -- pilot r1/r1.1: per-page discard + the recorded use-anyway override ----
  // The server decides hard-vs-soft from signature coverage and reports it as
  // `mode`; the confirmation phrases what ACTUALLY happened (owner decision
  // 2026-08-21): a pre-signature page is deleted for real, a signed-over page
  // is only removed from this verification (bytes retained).
  const handleDiscardPage = useCallback(
    async (page: CapturePage, intent: 'remove' | 'retake') => {
      const id = sessionId;
      if (!id || !page.discardAssetId || discardBusyKey) return;
      setDiscardBusyKey(page.key);
      try {
        const result = await ixv.discardSessionAsset(id, page.discardAssetId);
        if (page.pageIndex != null) releasePreview(page.pageIndex);
        setPromptDismissedAt(null);
        const fresh = await refreshDetail(id);
        // Surviving pages → re-read so fields + the capture verdict reflect
        // what is actually left (the same trigger a new page firing has).
        const docsLeft = (fresh.assets || []).some(
          (a) => !a.discarded_at && ['ixrs_photo', 'ixrs_document', 'ixrs_text'].includes(a.kind)
        );
        if (docsLeft) void runExtractionRef.current();
        if (intent === 'retake') {
          setMode('document');
          setView('capture');
        }
        const confirmation =
          result.mode === 'deleted'
            ? intent === 'retake'
              ? 'Photo deleted — retake it now'
              : 'Photo deleted'
            : intent === 'retake'
              ? 'Photo removed from this verification — retake it now'
              : 'Photo removed from this verification';
        toast(confirmation, { type: 'success' });
      } catch (err) {
        toast(ixv.errorMessage(err, 'Could not remove the page'), { type: 'error' });
      } finally {
        setDiscardBusyKey(null);
      }
    },
    [sessionId, discardBusyKey, releasePreview, refreshDetail, toast]
  );

  const handleOverrideCapture = useCallback(async () => {
    const id = sessionId;
    if (!id || overridingCapture) return;
    setOverridingCapture(true);
    try {
      await ixv.overrideCaptureCheck(id);
      await refreshDetail(id);
      toast('Photo kept — your decision was recorded', { type: 'success' });
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not record the override'), { type: 'error' });
    } finally {
      setOverridingCapture(false);
    }
  }, [sessionId, overridingCapture, refreshDetail, toast]);

  const tickForKitResult = useCallback((result: ixv.KitScanResult, scannedLabel?: string) => {
    const kit = result.kit || (result.scans && result.scans[result.scans.length - 1]);
    const label = kit?.scanned_kit_number || scannedLabel || '';
    if (result.duplicate) return `${label} — already scanned`;
    if (!kit) return label ? `${label} recorded` : 'Kit recorded';
    switch (kit.match_status) {
      case 'matched':
        return `${label} ✓ matched`;
      case 'manual_confirmed':
        return `${label} ✓ confirmed`;
      case 'pending':
        return `${label} recorded`;
      default:
        return `${label} — needs review`;
    }
  }, []);

  const applyKitResult = useCallback((result: ixv.KitScanResult) => {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            session: { ...prev.session, ...result.session },
            kits: result.kits,
            kit_state: result.kit_state,
          }
        : prev
    );
  }, []);

  const handleBarcode = useCallback(
    (payload: string) => {
      const id = sessionId;
      if (!id) return;
      // Serialize kit posts — rapid multi-scan must not race the reconciler.
      kitQueueRef.current = kitQueueRef.current.then(async () => {
        try {
          const result = await ixv.addKitFromBarcode(id, payload, replaceKitId || undefined);
          setReplaceKitId(null);
          applyKitResult(result);
          setLastKitTick(tickForKitResult(result));
          if (result.barcode?.expiry && result.barcode.warnings?.some((w) => /expir/i.test(w))) {
            toast('This kit may be expired — check the label date', { type: 'error' });
          }
        } catch (err) {
          if (ixv.errorCode(err) === 'IXV_BARCODE_UNREADABLE') setLastKitTick('Barcode unreadable — try the shutter');
          else if (ixv.isKillSwitched(err)) toast('IXVerify is currently disabled', { type: 'error' });
          else setLastKitTick(ixv.errorMessage(err, 'Scan failed'));
        }
      });
    },
    [sessionId, replaceKitId, applyKitResult, tickForKitResult, toast]
  );

  const handleLabelFrame = useCallback(
    async (frame: CapturedFrame) => {
      const id = sessionId;
      if (!id) return;
      setReadingLabel(true);
      try {
        const result = await ixv.addKitFromPhoto(id, frame.blob, { replaceKitId: replaceKitId || undefined });
        setReplaceKitId(null);
        applyKitResult(result);
        setLastKitTick(tickForKitResult(result));
      } catch (err) {
        const disagreement = ixv.sourcesDisagreement(err);
        if (disagreement) {
          setLastKitTick(`Readings disagree: ${disagreement.barcode.join('/')} vs ${disagreement.vision.join('/')} — rescan`);
        } else if (ixv.errorCode(err) === 'IXV_LABEL_UNREADABLE') {
          setLastKitTick('Label unreadable — retake or type it');
        } else if (ixv.isKillSwitched(err)) {
          toast('IXVerify AI is currently disabled — type the kit number instead', { type: 'error' });
        } else {
          setLastKitTick(ixv.errorMessage(err, 'Label read failed'));
        }
      } finally {
        setReadingLabel(false);
      }
    },
    [sessionId, replaceKitId, applyKitResult, tickForKitResult, toast]
  );

  const handleLabelFile = useCallback(
    (file: File) => {
      void handleLabelFrame({ blob: file, width: 0, height: 0 });
    },
    [handleLabelFrame]
  );

  const submitManualKit = useCallback(async () => {
    const id = sessionId;
    if (!id || !manualKitNumber.trim() || !manualKitReason.trim()) return;
    try {
      const result = await ixv.addKitManual(id, manualKitNumber.trim(), manualKitReason.trim(), replaceKitId || undefined);
      setReplaceKitId(null);
      applyKitResult(result);
      setLastKitTick(tickForKitResult(result));
      setManualKitOpen(false);
      setManualKitNumber('');
      setManualKitReason('');
    } catch (err) {
      toast(ixv.errorMessage(err, 'Manual entry failed'), { type: 'error' });
    }
  }, [sessionId, manualKitNumber, manualKitReason, replaceKitId, applyKitResult, tickForKitResult, toast]);

  const submitPaste = useCallback(async () => {
    const id = sessionId;
    if (!id || !pasteText.trim()) return;
    try {
      await ixv.addSessionText(id, pasteText.trim());
      setPasteOpen(false);
      setPasteText('');
      await refreshDetail(id);
      void runExtractionRef.current();
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not add the pasted text'), { type: 'error' });
    }
  }, [sessionId, pasteText, refreshDetail, toast]);

  // -- review handlers -------------------------------------------------------
  const extraction = detail?.session.extraction ?? null;
  // Status is the one truth (pilot r4): after a retake / discard / re-extract
  // the server demotes review → capturing but keeps the historical
  // assignment.confirmed_at — the old confirmation no longer covers the
  // evidence, so the Confirm CTA must come back and the attest gate close.
  const confirmed = detail?.session.status === 'review';

  // A fresh extraction is a fresh kit proposal — stale pre-confirm edits must
  // not silently override it.
  const extractedAt = extraction?.extracted_at ?? null;
  useEffect(() => {
    setKitDraft(null);
  }, [extractedAt]);

  const handleKitDraftChange = useCallback((kits: string[]) => {
    setKitDraft(kits);
    // Editing the proposed list IS the human touch on the kits exception (R5).
    setTouched((prev) => new Set(prev).add('kits'));
  }, []);

  const handleConfirm = useCallback(async () => {
    const id = sessionId;
    if (!id || !extraction) return;
    setConfirming(true);
    try {
      const draftKits = kitDraft ? kitDraft.map((k) => k.trim()).filter(Boolean) : null;
      const payload = confirmPayloadFromExtraction(extraction, edits, draftKits && draftKits.length > 0 ? draftKits : null);
      const result = await ixv.confirmSession(id, { fields: payload.fields, kits: payload.kits });
      setSubjectMatch(result.subject_match);
      // WP-5.1 contract: the confirm response carries teaching_mode; the
      // pre-WP-5.1 study_created heuristic stays as the fallback.
      setTeaching(result.teaching_mode ?? result.study_created);
      setDetail((prev) =>
        prev
          ? { ...prev, session: result.session, kits: result.kits, kit_state: result.kit_state }
          : prev
      );
      // Subject may have re-resolved server-side — pull the full picture.
      await refreshDetail(id);
      // An unknown subject is shown INLINE in the Review screen's Subject
      // section (and listed under "Before you can attest") with its own
      // "Add subject" button — the sheet no longer pops open on its own
      // (owner feedback 2026-10-03: no dialogs that interrupt).
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not confirm the assignment'), { type: 'error' });
    } finally {
      setConfirming(false);
    }
  }, [sessionId, extraction, edits, kitDraft, refreshDetail, toast]);

  const handleAddSubject = useCallback(
    async (fields: { subject_number: string; dob: string; initials?: string }) => {
      const id = sessionId;
      if (!id) return;
      setSubjectSaving(true);
      setSubjectError(null);
      try {
        await ixv.attachSessionSubject(id, fields);
        setSubjectSheetOpen(false);
        setSubjectMatch('matched');
        await refreshDetail(id);
      } catch (err) {
        setSubjectError(ixv.errorMessage(err, 'Could not add the subject'));
      } finally {
        setSubjectSaving(false);
      }
    },
    [sessionId, refreshDetail]
  );

  /** DOB-only sheet submit: the roster row exists, key its DOB (audited
   *  subject_updated; clears any prior admin confirmation server-side). */
  const handleEnterDob = useCallback(
    async (fields: { dob: string }) => {
      const id = sessionId;
      const subjectId = detail?.subject?.id;
      if (!id || !subjectId) return;
      setSubjectSaving(true);
      setSubjectError(null);
      try {
        await ixv.updateSubject(subjectId, { dob: fields.dob });
        setSubjectSheetOpen(false);
        await refreshDetail(id);
      } catch (err) {
        setSubjectError(ixv.errorMessage(err, 'Could not save the date of birth'));
      } finally {
        setSubjectSaving(false);
      }
    },
    [sessionId, detail?.subject?.id, refreshDetail]
  );

  const handleKitDecision = useCallback(
    async (kitId: string, decision: ixv.KitDecision, reason: string) => {
      const id = sessionId;
      if (!id) return;
      try {
        const result = await ixv.decideKit(id, kitId, decision, reason);
        setDetail((prev) => (prev ? { ...prev, kits: result.kits, kit_state: result.kit_state } : prev));
      } catch (err) {
        toast(ixv.errorMessage(err, 'Could not record the decision'), { type: 'error' });
      }
    },
    [sessionId, toast]
  );

  const handleRescanKit = useCallback((kitId: string) => {
    setReplaceKitId(kitId);
    setMode('label');
    setView('capture');
  }, []);

  const handleRecordMismatch = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    try {
      await ixv.recordMismatch(id);
      await refreshDetail(id);
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not record the mismatch'), { type: 'error' });
    }
  }, [sessionId, refreshDetail, toast]);

  // Pilot r3: the audited way back out of a recorded mismatch. The server
  // returns the session to review/capturing; the record itself stays on file.
  const handleReopenMismatch = useCallback(
    async (reason: string) => {
      const id = sessionId;
      if (!id) return;
      setReopening(true);
      try {
        const { session } = await ixv.reopenMismatch(id, reason);
        setView(session.status === 'review' ? 'review' : 'capture');
        await refreshDetail(id);
        toast('Mismatch reopened — correct the record, then verify again');
      } catch (err) {
        toast(ixv.errorMessage(err, 'Could not reopen the mismatch'), { type: 'error' });
      } finally {
        setReopening(false);
      }
    },
    [sessionId, refreshDetail, toast]
  );

  // Pilot r3: correct the ASSIGNED kit list (the misread case — e.g. the lot
  // number extracted and confirmed as the kit number). Saving re-confirms the
  // assignment with the corrected list; the server records the correction and
  // re-reconciles every standing scan against it.
  const handleOpenAssignedKits = useCallback(() => {
    const d = detailRef.current;
    const confirmedKits = d?.session.assignment?.kits?.map((k) => k.kit_number);
    const extractionKits = (d?.session.extraction?.kits || [])
      .map((k) => k.kit_number?.value)
      .filter((v): v is string => v != null && String(v).trim() !== '');
    const current = confirmedKits && confirmedKits.length > 0 ? confirmedKits : extractionKits;
    setAssignedKitEdits(current.length > 0 ? [...current] : ['']);
    // Seed the field inputs: the confirmed finals when they exist, else the
    // extraction proposal overlaid with any pending pre-confirm edits.
    const ex = d?.session.extraction ?? null;
    const seed = d?.session.assignment?.confirmed_at
      ? (d.session.assignment.fields as Record<ReviewFieldName, string | null>)
      : ex
        ? confirmPayloadFromExtraction(ex, editsRef.current).fields
        : null;
    setAssignedFieldEdits({
      protocol_id: seed?.protocol_id ?? '',
      subject_number: seed?.subject_number ?? '',
      visit_name: seed?.visit_name ?? '',
      dispense_date: seed?.dispense_date ?? '',
    });
    setAssignedEditOpen(true);
  }, []);

  const handleSaveAssignedKits = useCallback(async () => {
    const id = sessionId;
    const d = detailRef.current;
    if (!id || !d) return;
    const kitNumbers = assignedKitEdits.map((k) => k.trim()).filter(Boolean);
    if (kitNumbers.length === 0) return;
    setSavingAssignedKits(true);
    try {
      // Fields are whatever the sheet now holds (seeded from the confirmed
      // finals / extraction proposal in handleOpenAssignedKits); a blank input
      // means "no value" — the server records any difference as a correction.
      const fields = {} as Record<ReviewFieldName, string | null>;
      for (const f of REVIEW_FIELDS) {
        const v = assignedFieldEdits[f].trim();
        fields[f] = v === '' ? null : v;
      }
      const descByNumber = new Map(
        (d.session.assignment?.kits || []).map((k) => [k.kit_number, k.description ?? null])
      );
      const result = await ixv.confirmSession(id, {
        fields,
        kits: kitNumbers.map((n) => ({ kit_number: n, description: descByNumber.get(n) ?? null })),
      });
      setAssignedEditOpen(false);
      setDetail((prev) =>
        prev
          ? { ...prev, session: result.session, kits: result.kits, kit_state: result.kit_state }
          : prev
      );
      await refreshDetail(id);
      toast('Assignment updated — the correction is recorded');
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not update the assignment'), { type: 'error' });
    } finally {
      setSavingAssignedKits(false);
    }
  }, [sessionId, assignedKitEdits, assignedFieldEdits, refreshDetail, toast]);

  // -- attest + inline publish (R11: one motion) ----------------------------
  const handleAttest = useCallback(
    async (claims: { kits_confirmed: boolean; dob_confirmed: boolean; copy_certified?: boolean }) => {
      const id = sessionId;
      if (!id) return;
      setPublishing(true);
      setAttestError(null);
      try {
        await ixv.attestSession(id, claims);
        const result = await ixv.completeSession(id);
        setPublishResult(result);
        setDetail((prev) => (prev ? { ...prev, session: { ...prev.session, ...result.session } } : prev));
      } catch (err) {
        const code = ixv.errorCode(err);
        if (code === 'IXV_MISMATCH_PRESENT' || code === 'IXV_KITS_UNRESOLVED') {
          setView('review');
          toast(ixv.errorMessage(err, 'Kits are not resolved yet'), { type: 'error' });
        } else if (code === 'IXV_CAPTURE_INCOMPLETE') {
          setView('capture');
          setMode('document');
          setPromptDismissedAt(null);
          toast('The capture check failed — retake or add the missing page', { type: 'error' });
        } else if (code === 'IXV_ILLEGAL_TRANSITION') {
          // Pilot r4: the evidence changed after the confirm (retake /
          // discard / re-extract demoted the session server-side) — route
          // back to review, where the refreshed status re-opens the
          // Confirm CTA. Never leave this as a dead-end error.
          setView('review');
          toast('The evidence changed since the assignment was confirmed — confirm it again, then attest', { type: 'error' });
        } else {
          setAttestError(ixv.errorMessage(err, 'Could not attest and publish'));
        }
        try {
          await refreshDetail(id);
        } catch {
          /* keep the current view */
        }
      } finally {
        setPublishing(false);
      }
    },
    [sessionId, refreshDetail, toast]
  );

  // Read the sealed PDF in place (ReportPreview presigns it inline); the
  // download stays available as a button inside the preview.
  const handleViewReport = useCallback(() => {
    if (sessionId) setReportOpen(true);
  }, [sessionId]);

  const handleShare = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    try {
      const { url } = await ixv.getReportUrl(id, { inline: true });
      if (navigator.share) await navigator.share({ title: 'IXVerify report', url });
      else window.open(url, '_blank', 'noopener');
    } catch {
      /* user cancelled the share sheet */
    }
  }, [sessionId]);

  // ✕ leaves. Nothing is lost: an in-flight session is resumable from the
  // dashboard's In-progress list, so there is no "Leave this dispense?"
  // dialog (owner feedback 2026-10-03). Cancelling for good is the header's
  // two-tap trash control (WizardHeader) → handleCancelSession.
  const handleExit = useCallback(() => {
    const status = detailRef.current?.session.status;
    const inFlight = !!status && !['completed', 'verified', 'second_check_pending', 'mismatch_recorded', 'cancelled'].includes(status);
    navigate('/ixverify');
    if (inFlight) toast('Dispense saved — pick it up from the dashboard', { type: 'info' });
  }, [navigate, toast]);

  const handleCancelSession = useCallback(async () => {
    const id = sessionId;
    if (!id) {
      navigate('/ixverify');
      return;
    }
    setCancelling(true);
    try {
      await ixv.cancelSession(id);
      navigate('/ixverify');
      toast('Dispense cancelled', { type: 'info' });
    } catch (err) {
      setCancelling(false);
      toast(ixv.errorMessage(err, 'Could not cancel the dispense'), { type: 'error' });
    }
  }, [sessionId, navigate, toast]);

  // -- derived view ----------------------------------------------------------
  const step: WizardStep = useMemo(() => {
    if (loadError) return 'closed';
    if (!detail) return 'start';
    const server = stepForSession(detail);
    if (server === 'published' || server === 'mismatch' || server === 'closed') return server;
    if (view) return view;
    return server === 'review' ? 'review' : 'capture';
  }, [detail, view, loadError]);

  const prompt = useMemo(() => {
    const p = capturePrompt(extraction);
    if (!p) return null;
    if (promptDismissedAt && extraction?.extracted_at === promptDismissedAt) return null;
    return p;
  }, [extraction, promptDismissedAt]);

  const progress = kitProgress(detail?.kit_state);
  const pendingOriginals = uploader?.pendingOriginalCount() ?? 0;
  const plan = detail ? attestPlan(detail, pendingOriginals) : null;
  const pages = useMemo(() => derivePages(detail?.assets, uploadTasks), [detail?.assets, uploadTasks]);
  // Pilot r4: Review is reachable the moment anything is captured — uploads
  // and extraction keep running underneath; the review screen fills in live.
  const canFinishCapture = pages.length > 0 || !!extraction || (detail?.kits.length ?? 0) > 0 || confirmed;

  // -- desktop drag-and-drop (cross-device step A) ---------------------------
  // The whole wizard is a drop target while capturing. A PDF always takes the
  // document path (the fast path — no R12 photo rules apply to a native PDF);
  // an image follows the current mode. dragenter/dragleave are counted
  // because they fire for every child the pointer crosses. Outside the
  // capture step a file drop is ignored — but still swallowed, so the browser
  // never navigates away to open the dropped file over a review or attest.
  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const dropActive = step === 'capture';
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      if (!dropActive) return;
      dragDepthRef.current += 1;
      setDragOver(true);
    },
    [dropActive]
  );
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = dropActive ? 'copy' : 'none';
    },
    [dropActive]
  );
  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!dropActive || !dragHasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragOver(false);
    },
    [dropActive]
  );
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setDragOver(false);
      if (!dropActive) return;
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      let skipped = 0;
      for (const file of files) {
        if (!DROP_MIME_TYPES.has(file.type)) {
          skipped += 1;
        } else if (file.type !== 'application/pdf' && mode === 'label') {
          handleLabelFile(file);
        } else {
          void handleDocumentFile(file);
        }
      }
      if (skipped > 0) {
        toast(
          skipped === files.length ? 'Drop a PDF, JPEG, or PNG' : `${skipped} file${skipped === 1 ? '' : 's'} skipped — only PDF, JPEG, or PNG`,
          { type: 'error' }
        );
      }
    },
    [dropActive, mode, handleDocumentFile, handleLabelFile, toast]
  );

  // -- render ----------------------------------------------------------------
  return (
    // h-[100dvh] pins the wizard to the VISIBLE viewport on mobile (pilot r4:
    // the browser toolbar was cutting off the bottom controls — the Review
    // button most painfully). Browsers without dvh drop the declaration and
    // fall back to inset-0's bottom anchoring.
    <div
      className="fixed inset-0 z-40 flex h-[100dvh] flex-col bg-gray-950"
      data-testid="dispense-wizard"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <WizardHeader
        step={step}
        siteName={currentSite?.name ?? null}
        onExit={handleExit}
        onCancel={detail && ['draft', 'staged', 'capturing', 'review'].includes(detail.session.status) ? () => void handleCancelSession() : undefined}
        cancelling={cancelling}
      />

      {/* Drop feedback (desktop): pointer-events-none so the drop still lands
          on the root; z-50 sits above the capture surface, below nothing that
          matters while dragging. */}
      {dragOver && dropActive && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-gray-950/80 p-6" data-testid="drop-overlay" aria-hidden="true">
          <div className="flex max-w-sm flex-col items-center gap-2 rounded-xl border-2 border-dashed border-primary-500 px-6 py-8 text-center text-white">
            <UploadCloud className="h-8 w-8 text-primary-400" aria-hidden="true" />
            <p className="text-sm font-semibold">Drop the IXRS assignment here</p>
            <p className="text-xs text-white/70">PDF, JPEG, or PNG. A PDF is read as the document; a photo follows the current mode.</p>
          </div>
        </div>
      )}

      {loadError ? (
        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-6 text-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
        >
          <p className="text-sm text-white/80">{loadError}</p>
          <button
            type="button"
            onClick={() => navigate('/ixverify')}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Back to dashboard
          </button>
        </div>
      ) : step === 'start' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-white/70">
          <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          <p className="text-sm">Opening dispense…</p>
        </div>
      ) : step === 'capture' ? (
        <CaptureSurface
          mode={mode}
          onModeChange={setMode}
          uploadTasks={uploadTasks}
          onRetryUpload={(taskId) => uploader?.retry(taskId)}
          pages={pages}
          pagePreviews={pagePreviews}
          onDiscardPage={(page, intent) => void handleDiscardPage(page, intent)}
          discardBusyKey={discardBusyKey}
          kitProgress={progress}
          lastKitTick={lastKitTick}
          prompt={prompt}
          onDismissPrompt={() => setPromptDismissedAt(extraction?.extracted_at ?? 'dismissed')}
          onOverrideCapture={() => void handleOverrideCapture()}
          overridingCapture={overridingCapture}
          extracting={extracting}
          readingLabel={readingLabel}
          onDocumentFrame={(f) => void handleDocumentFrame(f)}
          onDocumentFile={(f) => void handleDocumentFile(f)}
          onPasteText={() => setPasteOpen(true)}
          onBarcodeDetected={handleBarcode}
          onLabelFrame={(f) => void handleLabelFrame(f)}
          onLabelFile={handleLabelFile}
          onManualKit={() => setManualKitOpen(true)}
          onDone={() => setView('review')}
          canFinish={canFinishCapture}
        />
      ) : step === 'review' && detail ? (
        <ReviewScreen
          detail={detail}
          extraction={extraction}
          extracting={extracting}
          subject={detail.subject}
          subjectMatch={subjectMatch ?? (detail.subject ? 'matched' : detail.session.site_study_id ? 'unknown' : 'no_study')}
          teaching={teaching}
          touched={touched}
          edits={edits}
          kitDraft={kitDraft}
          onKitDraftChange={handleKitDraftChange}
          onEditField={(f, v) => {
            setEdits((prev) => ({ ...prev, [f]: v }));
            setTouched((prev) => new Set(prev).add(f));
          }}
          onAcceptField={(f) => setTouched((prev) => new Set(prev).add(f))}
          confirmed={confirmed}
          confirming={confirming}
          onConfirm={() => void handleConfirm()}
          onAddSubject={() => {
            setSubjectError(null);
            setSubjectSheetMode('add');
            setSubjectSheetOpen(true);
          }}
          onEnterDob={() => {
            setSubjectError(null);
            setSubjectSheetMode('dob');
            setSubjectSheetOpen(true);
          }}
          onKitDecision={(kitId, decision, reason) => void handleKitDecision(kitId, decision, reason)}
          onRescanKit={handleRescanKit}
          onBackToCapture={() => setView('capture')}
          onProceed={() => setView('attest')}
          onRecordMismatch={() => void handleRecordMismatch()}
          onEditAssignedKits={handleOpenAssignedKits}
          canProceed={!!plan?.ready}
          plan={plan}
          onOverrideCapture={() => void handleOverrideCapture()}
          overridingCapture={overridingCapture}
        />
      ) : step === 'attest' && detail && plan ? (
        <AttestScreen
          subject={detail.subject}
          plan={plan}
          publishing={publishing}
          error={attestError}
          onBack={() => setView('review')}
          onAttest={(claims) => void handleAttest(claims)}
        />
      ) : step === 'published' && detail ? (
        publishResult ? (
          <SuccessScreen
            result={publishResult}
            onViewReport={handleViewReport}
            onShare={() => void handleShare()}
            canShare={typeof navigator !== 'undefined' && !!navigator.share}
            onRequestSecondCheck={() => setSecondCheckOpen(true)}
            onNewDispense={() => {
              window.location.assign('/ixverify/dispense');
            }}
            onDone={() => navigate('/ixverify')}
          />
        ) : (
          /* Reopened later: the published summary, not the celebration. */
          <div
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-6 text-center text-white"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
          >
            <p className="text-lg font-semibold tabular-nums">{detail.session.verification_code}</p>
            <p className="text-sm text-white/80">This dispense is published and sealed.</p>
            <button
              type="button"
              onClick={handleViewReport}
              className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              View report PDF
            </button>
            <button
              type="button"
              onClick={() => navigate('/ixverify')}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Back to dashboard
            </button>
          </div>
        )
      ) : step === 'mismatch' ? (
        <MismatchRecordedScreen
          detail={detail}
          onDone={() => navigate('/ixverify')}
          onReopen={(reason) => void handleReopenMismatch(reason)}
          reopening={reopening}
        />
      ) : (
        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-6 text-center text-white/80"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
        >
          <p className="text-sm">This session is closed.</p>
          <button
            type="button"
            onClick={() => navigate('/ixverify')}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Back to dashboard
          </button>
        </div>
      )}

      {/* Sheets */}
      {subjectSheetOpen && (
        <SubjectSheet
          mode={subjectSheetMode}
          prefillSubjectNumber={
            subjectSheetMode === 'dob'
              ? detail?.subject?.subject_number || ''
              : extraction?.fields?.subject_number?.value || ''
          }
          saving={subjectSaving}
          error={subjectError}
          onClose={() => setSubjectSheetOpen(false)}
          onSubmit={(fields) => void (subjectSheetMode === 'dob' ? handleEnterDob({ dob: fields.dob }) : handleAddSubject(fields))}
        />
      )}
      {reportOpen && sessionId && (
        <ReportPreview
          sessionId={sessionId}
          verificationCode={detail?.session.verification_code ?? null}
          onClose={() => setReportOpen(false)}
        />
      )}
      {secondCheckOpen && sessionId && (
        <SecondCheckSheet
          sessionId={sessionId}
          onClose={() => setSecondCheckOpen(false)}
          onRequested={() => {
            setSecondCheckOpen(false);
            toast('Second check requested', { type: 'success' });
          }}
        />
      )}

      {/* Manual kit entry (scan_method='manual' — reason required, renders
          amber on the report's provenance box) */}
      {manualKitOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Type a kit number"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Type a kit number</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manual entries are flagged on the report — a reason is required.
            </p>
            <input
              aria-label="Kit number"
              className={`${fieldInputCls} mt-3 font-mono`}
              placeholder="Kit number"
              value={manualKitNumber}
              onChange={(e) => setManualKitNumber(e.target.value)}
            />
            <textarea
              aria-label="Reason for manual entry"
              className={`${fieldInputCls} mt-2`}
              rows={2}
              placeholder="Why manual? (e.g. barcode damaged)"
              value={manualKitReason}
              onChange={(e) => setManualKitReason(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setManualKitOpen(false)}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!manualKitNumber.trim() || !manualKitReason.trim()}
                onClick={() => void submitManualKit()}
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                data-testid="manual-kit-save"
              >
                Add kit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit the ASSIGNED kit list (pilot r3 — the misread case: fix what
          was confirmed from the document, e.g. a lot number read as the kit
          number). Saving re-confirms; the correction is recorded server-side
          and every standing scan re-pairs against the corrected list. */}
      {assignedEditOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Edit assignment"
        >
          <div className="w-full max-w-sm max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit assignment</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              What was read from the IXRS assignment document. Fix any misread — a wrong subject or
              visit, or a lot number read as a kit number. The correction is recorded and the scans
              re-check against the corrected kit list.
            </p>
            <div className="mt-3 space-y-2" data-testid="assigned-fields-editor">
              {REVIEW_FIELDS.map((f) => (
                <label key={f} className="block">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{FIELD_LABEL[f]}</span>
                  <input
                    aria-label={FIELD_LABEL[f]}
                    className={`${fieldInputCls} mt-0.5 ${f === 'dispense_date' ? '' : 'font-mono'}`}
                    value={assignedFieldEdits[f]}
                    onChange={(e) => setAssignedFieldEdits((prev) => ({ ...prev, [f]: e.target.value }))}
                    data-testid={`assigned-field-${f}`}
                  />
                </label>
              ))}
            </div>
            <p className="mt-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Assigned kits</p>
            <div className="mt-1.5 space-y-2">
              {assignedKitEdits.map((value, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    aria-label={`Assigned kit ${i + 1}`}
                    className={`${fieldInputCls} font-mono`}
                    placeholder="Kit number"
                    value={value}
                    onChange={(e) =>
                      setAssignedKitEdits((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Remove assigned kit ${i + 1}`}
                    disabled={assignedKitEdits.length === 1}
                    onClick={() => setAssignedKitEdits((prev) => prev.filter((_, j) => j !== i))}
                    className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setAssignedKitEdits((prev) => [...prev, ''])}
              className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add kit
            </button>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setAssignedEditOpen(false)}
                disabled={savingAssignedKits}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingAssignedKits || assignedKitEdits.every((k) => !k.trim())}
                onClick={() => void handleSaveAssignedKits()}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                data-testid="assigned-kits-save"
              >
                {savingAssignedKits && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                Save corrected list
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paste assignment text */}
      {pasteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Paste assignment text"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Paste assignment text</h3>
            <textarea
              aria-label="Assignment text"
              className={`${fieldInputCls} mt-3 font-mono`}
              rows={6}
              placeholder="Paste the IXRS assignment email or text here"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPasteOpen(false)}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!pasteText.trim()}
                onClick={() => void submitPaste()}
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                Use this text
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit sheet — leaving mid-dispense is safe (sessions resume) */}
    </div>
  );
}
